import { Box, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { Fragment, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { bindActionCreators } from 'redux';
import { mutate } from 'swr/_internal';
import CreateFieldStepper from '/@/components/crop-monitoring/create-field-stepper';
import DataSourceStepper from '/@/components/crop-monitoring/data-source-stepper';
import EnvironmentStepper from '/@/components/crop-monitoring/environment-stepper';
import StepChooseFertilizer from '/@/components/crop-monitoring/step-choose-fertilizer';
import StepChooseHybrid from '/@/components/crop-monitoring/step-choose-hybrid';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { setComesFromNotification, setField, setHybrid, setRecommendation } from '/@/store/crop-monitoring/actions';
import {
    getBtnDisabled,
    getComesFromNotification,
    getEnvironment,
    getFertilizer,
    getFertilizerAmount,
    getField,
    getHybrid,
    getPlantingDate,
    getRecommendation,
} from '/@/store/crop-monitoring/selectors';
import { setGoBack } from '/@/store/nav/actions';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import useWindowSize from '/@/utils/hooks/window-size';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';
import Alert from '../../bayer/alert';
import styles from '../styles.module.css';

const CropMonitoringStepper = () => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const windowWidth = useWindowSize().width;
    const isMobile = windowWidth <= 900;
    const isMobileXl = windowWidth >= 612;
    const fieldId = useSelector(getField);
    const appInstance = useSelector(getAppInstance);
    const recommendation = useSelector(getRecommendation);
    const disabled = useSelector(getBtnDisabled);
    const { data: field, isValidating: fieldIsValidating } = useFieldById({ id: fieldId }, { enabled: !!fieldId });
    const [activeStep, setActiveStep] = useState(0);
    const [data, setData] = useState({});
    const [error, setError] = useState();
    const setFieldDispatch = bindActionCreators(setField, dispatch);
    const setRecoDispatch = bindActionCreators(setRecommendation, dispatch);
    const setHybridDispatch = bindActionCreators(setHybrid, dispatch);
    const country = useSelector(getSessionCountry);

    const plantingDate = useSelector(getPlantingDate);
    const environment = useSelector(getEnvironment);
    const hybrid = useSelector(getHybrid);
    const fertilizer = useSelector(getFertilizer);
    const fertilizerAmount = useSelector(getFertilizerAmount);

    const comesFromNotification = useSelector(getComesFromNotification);

    const steps = useMemo(() => {
        const _steps = [
            {
                title: _('**Seleccionar o Crear Lote** *'),
                description: _(
                    'Elegí uno de tus Lotes para poder contar con la información del contexto en donde se desarrollarán tus Cultivos.'
                ),
                component: CreateFieldStepper,
            },
            {
                title: _('**Elegir el origen de los Datos** *'),
                description: _(
                    'Para el formulario de carga, necesitaremos información como la ***Fecha de siembra*** y características generales del ***Ambiente***, podes cargar los datos de Manera Manual, utilizar una ***Recomendación de Maíz*** generada anteriormente (podras editarlos) o crear una ***Nueva*** y utilizar esa información para el Seguimiento.'
                ),
                component: DataSourceStepper,
            },
            {
                title: _('**Cargar fecha de siembra** *'),
                description: null,
                alert: _(
                    'Los datos fueron completados con la información de la Recomendación que generaste, pero podés Modificar el campo que creas necesario.'
                ),
                component: EnvironmentStepper,
            },
            {
                title: _('**Escoger Producto Sembrado** *'),
                description: null,
                component: StepChooseHybrid,
            },
        ];
        if (country.code !== 'BR')
            _steps.push({
                title: _('**Ingresar Fertilizante a la Siembra** *(Opcional)*'),
                description: _('Llegamos al último paso, podés completarlo **ahora** o **más tarde.**'),
                component: StepChooseFertilizer,
            });

        return _steps;
    }, [country]);

    useEffect(() => {
        if (field && !fieldIsValidating && activeStep == 0) {
            handleNext();
        }
    }, [field, fieldIsValidating, recommendation]);

    useEffect(() => {
        if (recommendation !== undefined && activeStep === 1) {
            handleNext();
        }
    }, [activeStep, recommendation]);

    useEffect(() => {
        if (!field && !fieldIsValidating) {
            setActiveStep(0);
        }
    }, [field, fieldIsValidating]);

    useEffect(() => {
        return () => {
            dispatch(setComesFromNotification(false));
        };
    }, []);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => {
            if (prevActiveStep === steps.length - 1) {
                createCropMonitoring();
            }

            return prevActiveStep + 1;
        });
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => {
            const newStep = prevActiveStep - 1;

            if (newStep === 1) {
                dispatch(setRecommendation(undefined));
            }

            return newStep;
        });
    };

    const handleFieldSelection = (field) => {
        setHybridDispatch(undefined);
        setFieldDispatch(field);
        setData({ ...data, field });
        handleNext();
    };

    const handleRecommendation = (reco) => {
        setRecoDispatch(reco);
        setData({ ...data, reco });
        handleNext();
    };

    const createCropMonitoring = async () => {
        try {
            const res = await axFetch.put(`${settings.urls.apiDomain}/core/v1/crop-monitoring/`, {
                plantingDate: plantingDate,
                environment,
                fertilizer_amount: fertilizerAmount,
                field: field.id,
                recommendation,
                product: hybrid,
                fertilizer,
                appInstance: appInstance.id,
            });

            dispatch(
                trackEvent({
                    key: 'cornCropMonitoring.create.finalize',
                    source: comesFromNotification ? 'desde notificación' : undefined,
                })
            );

            // Redirigir a otra ruta

            if (settings.app.theme === 'expertobayer') {
                history(
                    reverse('fields.FieldDetail', {
                        params: { fieldId: res.data.field },
                    })
                );
            } else {
                history(
                    reverse('cropMonitoring.CornCropMonitoringDetail', { params: { cropMonitoringId: res.data.id } })
                );
            }

            dispatch(() => setGoBack(history(reverse('cropMonitoring.CropMonitoring'), { replace: true })));
            setError(false);
            mutate({ key: 'allCropMonitorings', appInstance: appInstance.id }, undefined, true);
        } catch (error) {
            setError(true);
            handleBack();
        }
    };

    const ActiveComponent = steps[activeStep] && steps?.[activeStep]?.component;

    return (
        <Fragment>
            <Stepper activeStep={activeStep} orientation={isMobile ? 'horizontal' : 'vertical'}>
                {steps.map((step, index) => (
                    <Step key={step.title}>
                        <StepLabel>
                            {!isMobile && (
                                <Grid container justifyContent='flex-start' spacing={2} sx={{ pl: 1 }}>
                                    <Grid item xs={12}>
                                        <Title className={styles.titleStep}>
                                            <ReactMarkdown className={styles.titleStep}>{_(step.title)}</ReactMarkdown>
                                        </Title>
                                    </Grid>
                                </Grid>
                            )}
                        </StepLabel>
                        {!isMobile && (
                            <StepContent>
                                <Grid container justifyContent='flex-start' spacing={2} sx={{ pl: 2 }}>
                                    <Grid item xs={12}>
                                        <Fragment>
                                            {index === 2 && recommendation && (
                                                <Alert severity='blue'>
                                                    {step.alert && <ReactMarkdown>{_(step.alert)}</ReactMarkdown>}
                                                </Alert>
                                            )}
                                            <Fragment>
                                                {Boolean(step.description) && (
                                                    <Grid>
                                                        <Title className={styles.descriptionStep}>
                                                            <ReactMarkdown className={styles.descriptionStep}>
                                                                {_(step.description)}
                                                            </ReactMarkdown>
                                                        </Title>
                                                    </Grid>
                                                )}
                                                {step.component && (
                                                    <step.component
                                                        onClick={(e) => handleFieldSelection(e)}
                                                        selected={fieldId} // FIELD ID
                                                        onRecoSelect={(e) => handleRecommendation(e)}
                                                        handleNext={handleNext}
                                                        handleBack={handleBack}
                                                        data={data}
                                                        setData={setData}
                                                        selectedFeature='cropMonitoring'
                                                    />
                                                )}
                                            </Fragment>
                                        </Fragment>
                                    </Grid>
                                    {index !== 0 && (
                                        <Fragment>
                                            {index !== 1 && (
                                                <Grid item>
                                                    <Button
                                                        onClick={handleNext}
                                                        disabled={disabled}
                                                        variant='contained'
                                                    >
                                                        {index === steps.length - 1
                                                            ? _('Crear seguimiento')
                                                            : _('Continuar')}
                                                    </Button>
                                                    {index === steps.length - 1 && error && (
                                                        <Box sx={{ m: 1 }}>
                                                            <Title color='error' size='sm'>
                                                                {_(
                                                                    'Ha ocurrido un error, por favor inténtalo más tarde.'
                                                                )}
                                                            </Title>
                                                        </Box>
                                                    )}
                                                </Grid>
                                            )}

                                            <Grid item>
                                                <Button disabled={index === 0} onClick={handleBack} variant='contained'>
                                                    {_('Volver')}
                                                </Button>
                                            </Grid>
                                        </Fragment>
                                    )}
                                </Grid>
                            </StepContent>
                        )}
                    </Step>
                ))}
            </Stepper>
            {isMobile && (
                <Fragment>
                    {steps[activeStep] && (
                        <Grid container justifyContent='flex-start' spacing={2} sx={{ p: 2 }}>
                            <Grid item xs={12} sx={{ pb: 10 }}>
                                <Fragment>
                                    <Fragment>
                                        {Boolean(steps?.[activeStep]?.title) && (
                                            <Grid sx={{ py: 2 }}>
                                                <Title className={styles.descriptionStep}>
                                                    <ReactMarkdown className={styles.descriptionStep}>
                                                        {_(steps?.[activeStep]?.title)}
                                                    </ReactMarkdown>
                                                </Title>
                                            </Grid>
                                        )}
                                        {Boolean(steps?.[activeStep]?.description) && (
                                            <Grid sx={{ pt: 2 }}>
                                                <Title className={styles.descriptionStep}>
                                                    <ReactMarkdown className={styles.descriptionStep}>
                                                        {_(steps?.[activeStep]?.description)}
                                                    </ReactMarkdown>
                                                </Title>
                                            </Grid>
                                        )}
                                        {activeStep === 2 && recommendation && (
                                            <Grid
                                                sx={{
                                                    position: 'relative',
                                                    left: isMobileXl ? '-48px' : '-32px',
                                                    width: '100vw',
                                                    pt: 2,
                                                }}
                                            >
                                                <Alert severity='blue'>
                                                    {steps?.[activeStep]?.alert && (
                                                        <ReactMarkdown className={styles.descriptionStep}>
                                                            {_(steps?.[activeStep]?.alert)}
                                                        </ReactMarkdown>
                                                    )}
                                                </Alert>
                                            </Grid>
                                        )}
                                        {steps?.[activeStep]?.component && (
                                            <ActiveComponent
                                                onClick={(e) => handleFieldSelection(e)}
                                                selected={fieldId}
                                                onRecoSelect={(e) => handleRecommendation(e)}
                                                handleNext={handleNext}
                                                data={data}
                                                setData={setData}
                                                isMobile={isMobile}
                                                handleBack={handleBack}
                                            />
                                        )}
                                    </Fragment>
                                </Fragment>
                            </Grid>
                            {activeStep !== 0 && (
                                <Fragment>
                                    <Grid
                                        container
                                        direction='column'
                                        sx={{
                                            width: '100%',
                                            position: 'fixed',
                                            bottom: 0,
                                            left: 0,
                                            py: 2,
                                            px: 4,
                                            borderTop: '0.5px solid #E1E1E1',
                                            zIndex: 2,
                                            bgcolor: '#FFFFFF',
                                            ...(isMobileXl && {
                                                px: 6,
                                            }),
                                        }}
                                    >
                                        {activeStep >= 2 && (
                                            <Grid item sx={{ pb: 2 }}>
                                                <Button
                                                    fullWidth
                                                    onClick={handleNext}
                                                    disabled={disabled}
                                                    variant='contained'
                                                >
                                                    {activeStep === steps.length - 1
                                                        ? _('Crear seguimiento')
                                                        : _('Siguiente')}
                                                </Button>
                                            </Grid>
                                        )}

                                        <Grid item>
                                            <Button
                                                fullWidth
                                                disabled={activeStep === 0}
                                                onClick={handleBack}
                                                variant='contained'
                                            >
                                                {_('Volver')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Fragment>
                            )}
                        </Grid>
                    )}
                </Fragment>
            )}
            {activeStep === steps.length && (
                <Paper square elevation={0} sx={isMobile ? { p: 2 } : { p: 3 }}>
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12} sx={{ pb: 2 }}>
                            <Title className={styles.finishSteps}>{_('Completaste todos los pasos')}</Title>
                        </Grid>
                        <Grid item>
                            <Button fullWidth disabled variant='contained'>
                                <div className={styles.flexCenter}>
                                    {_('Creando seguimiento...')} <Spinner className={styles.spinner} />
                                </div>
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}
        </Fragment>
    );
};

export default CropMonitoringStepper;
